// /settings/notifications — Notification Preferences (PAGES.md §3.79)
//
// Per-channel flat master switches (in-app is always on), plus the G2.12
// Web Push opt-in flow. Auto-saves on every toggle.
//
// Vertical: SHARED
// Min role: CASHIER (5) — enforced by the router. Per FR-NOTIF-008 every
// user opts in/out of their own delivery channels. Platform users
// (SUPER_ADMIN / ADMIN / MODERATOR) see the page but the browser-push
// channel is hidden because the backend `POST /push/subscriptions`
// endpoint requires tenant context. The per-(type × channel) matrix is
// rendered only for tenant users at MANAGER (30) and above (G2.10).
//
// Traceability: FR-NOTIF-005 to FR-NOTIF-010, G2.10 (matrix), G2.12 (push)

import { useState, useCallback, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { PageContainer, Skeleton, Toggle, ErrorBoundary, useToast } from '@bbpos/ui';
import { useAuthStore } from '@/stores/use-auth-store';
import {
  notificationService,
  type NotificationPreferences,
} from '@/services/notification.service';
import { ApiError } from '@/lib/api-client';
import { GLASS_FORM, GLASS, cn } from '@/constants/design-tokens';
import {
  enablePushNotifications,
  disablePushNotifications,
  isPushSupported,
  getPushPermission,
} from '@/lib/push-notifications';
import {
  PushStatusRow,
  type PushUiState,
} from '@/components/features/notifications/PushStatusRow';
import { NotificationMatrixCard } from './NotificationMatrixCard';

const DEFAULT_PREFS: NotificationPreferences = {
  email_enabled: true,
  sms_enabled: true,
  whatsapp_enabled: true,
  push_enabled: false,
};

type ChannelTone = 'brand' | 'teal' | 'emerald' | 'violet';

interface ChannelDef {
  key: keyof NotificationPreferences;
  label: string;
  description: string;
  iconPath: string;
  tone: ChannelTone;
}

const CHANNELS: ChannelDef[] = [
  {
    key: 'email_enabled',
    label: 'Email',
    description: 'Transactional and digest emails via your verified email account',
    iconPath: 'M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z',
    tone: 'brand',
  },
  {
    key: 'sms_enabled',
    label: 'SMS',
    description: 'Text-message alerts via Twilio',
    iconPath: 'M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z',
    tone: 'teal',
  },
  {
    key: 'whatsapp_enabled',
    label: 'WhatsApp',
    description: 'Notifications via the WhatsApp Business API',
    iconPath: 'M21 11.5a8.38 8.38 0 01-.9 3.8 8.5 8.5 0 01-7.6 4.7 8.38 8.38 0 01-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 01-.9-3.8 8.5 8.5 0 014.7-7.6 8.38 8.38 0 013.8-.9h.5a8.48 8.48 0 018 8v.5z',
    tone: 'emerald',
  },
  {
    key: 'push_enabled',
    label: 'Browser Push',
    description: 'OS-level notifications on this device (VAPID / Web Push)',
    iconPath: 'M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9M13.73 21a2 2 0 01-3.46 0',
    tone: 'violet',
  },
];

const PLATFORM_ROLES = new Set<string>(['SUPER_ADMIN', 'ADMIN', 'MODERATOR']);

/** Left-accent rail colour per channel — mirrors the CLAUDE.md domain palette. */
function channelAccent(tone: ChannelTone): string {
  if (tone === 'brand') return 'before:bg-brand-500';
  if (tone === 'teal') return 'before:bg-teal-500';
  if (tone === 'emerald') return 'before:bg-emerald-500';
  return 'before:bg-violet-500';
}

function channelIconHalo(tone: ChannelTone): string {
  if (tone === 'brand')
    return 'bg-brand-50 dark:bg-brand-900/30 text-brand-600 dark:text-brand-300';
  if (tone === 'teal')
    return 'bg-teal-50 dark:bg-teal-900/30 text-teal-600 dark:text-teal-300';
  if (tone === 'emerald')
    return 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-300';
  return 'bg-violet-50 dark:bg-violet-900/30 text-violet-600 dark:text-violet-300';
}

function NotificationPrefsContent() {
  const { toast } = useToast();
  const profile = useAuthStore((s) => s.profile);
  const userRole = profile?.role ?? '';
  const canUsePush = !PLATFORM_ROLES.has(userRole);
  // Matrix card is tenant-scoped and MANAGER+ only. CASHIER sees just the
  // four flat channel switches (per FR-NOTIF-008).
  const canSeeMatrix = canUsePush && userRole !== 'CASHIER';

  const [prefs, setPrefs] = useState<NotificationPreferences>(DEFAULT_PREFS);
  const [isLoading, setIsLoading] = useState(true);
  const [savingKey, setSavingKey] = useState<string | null>(null);

  // G2.12 — surfaces the result of enablePushNotifications() under the toggle.
  const [pushUiState, setPushUiState] = useState<PushUiState>('idle');
  const [pushError, setPushError] = useState<string | undefined>(undefined);
  const [recheckNonce, setRecheckNonce] = useState(0);

  // Debounce the PATCH so rapid clicks collapse to a single request.
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    document.title = 'Notification Preferences — Settings — Orio360 Admin';
  }, []);

  // Load the flat master switches from the backend.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await notificationService.getPreferences();
        if (!cancelled) setPrefs(data);
      } catch (err) {
        if (!cancelled) {
          toast({
            title: err instanceof ApiError ? err.message : 'Failed to load preferences',
            variant: 'error',
          });
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [toast]);

  // G2.12 — silent re-check: if the server says push_enabled=true but the
  // local subscription is gone (browser key rotation, cleared storage),
  // surface the "lost" warning so the user can re-enable with one tap.
  useEffect(() => {
    if (isLoading || !prefs.push_enabled || !canUsePush) return;
    let cancelled = false;
    (async () => {
      if (!isPushSupported()) {
        if (!cancelled) setPushUiState('unsupported');
        return;
      }
      if (getPushPermission() !== 'granted') {
        if (!cancelled) setPushUiState('lost');
        return;
      }
      try {
        const reg = await navigator.serviceWorker.ready;
        const sub = await reg.pushManager.getSubscription();
        if (!cancelled) setPushUiState(sub ? 'subscribed' : 'lost');
      } catch {
        if (!cancelled) setPushUiState('lost');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isLoading, prefs.push_enabled, canUsePush, recheckNonce]);

  // G2.12 — listen for the SW broadcast fired on `pushsubscriptionchange`
  // and force a silent re-check so the UI flips to `lost`.
  useEffect(() => {
    if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return;
    const handler = (ev: MessageEvent) => {
      if (ev.data?.type === 'bbpos.push.resubscribe-needed') {
        setPushUiState('lost');
        setRecheckNonce((n) => n + 1);
      }
    };
    navigator.serviceWorker.addEventListener('message', handler);
    return () => navigator.serviceWorker.removeEventListener('message', handler);
  }, []);

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const persistPref = useCallback(
    async (key: keyof NotificationPreferences, value: boolean) => {
      await notificationService.updatePreferences({ [key]: value });
    },
    [],
  );

  const handleToggle = useCallback(
    async (key: keyof NotificationPreferences, value: boolean) => {
      // G2.12 — push_enabled drives the SW + backend subscription dance
      // BEFORE flipping the flat preference flag.
      if (key === 'push_enabled') {
        setSavingKey(key);
        try {
          if (value) {
            setPushUiState('pending');
            setPushError(undefined);
            const result = await enablePushNotifications();
            setPushUiState(result.status);
            setPushError(result.error);
            if (result.status === 'subscribed') {
              setPrefs((prev) => ({ ...prev, push_enabled: true }));
              await persistPref('push_enabled', true);
              toast({ title: 'Browser push enabled', variant: 'success' });
            } else {
              // Subscription failed — leave the toggle off and let the
              // status row explain why.
              setPrefs((prev) => ({ ...prev, push_enabled: false }));
            }
          } else {
            await disablePushNotifications();
            setPrefs((prev) => ({ ...prev, push_enabled: false }));
            await persistPref('push_enabled', false);
            setPushUiState('idle');
            setPushError(undefined);
            toast({ title: 'Browser push disabled', variant: 'success' });
          }
        } catch (err) {
          setPrefs((prev) => ({ ...prev, push_enabled: !value }));
          toast({
            title: err instanceof ApiError ? err.message : 'Failed to update browser push',
            variant: 'error',
          });
          setPushUiState('error');
          setPushError(err instanceof Error ? err.message : undefined);
        } finally {
          setSavingKey(null);
        }
        return;
      }

      // Other channels — optimistic flat-preference toggle with debounce.
      setPrefs((prev) => ({ ...prev, [key]: value }));
      setSavingKey(key);
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(async () => {
        try {
          await persistPref(key, value);
          toast({ title: 'Preference saved', variant: 'success' });
        } catch (err) {
          setPrefs((prev) => ({ ...prev, [key]: !value }));
          toast({
            title: err instanceof ApiError ? err.message : 'Failed to save preference',
            variant: 'error',
          });
        } finally {
          setSavingKey(null);
        }
      }, 400);
    },
    [persistPref, toast],
  );

  const visibleChannels = CHANNELS.filter((ch) => canUsePush || ch.key !== 'push_enabled');

  return (
    <PageContainer
      title="Notification Preferences"
      breadcrumbs={[
        { label: 'Home', href: '/home' },
        { label: 'Settings', href: '/settings' },
        { label: 'Notifications' },
      ]}
    >
      <div className="animate-fade-in">
        <Link
          to="/settings"
          className="text-sm text-brand-600 dark:text-brand-400 hover:underline mb-4 inline-block"
        >
          ← Back to Settings
        </Link>

        {/* Fintech-authoritative gradient header */}
        <div className="bg-gradient-to-r from-sky-700 via-indigo-700 to-violet-700 rounded-2xl p-6 text-white shadow-[0_20px_60px_-20px_rgba(15,23,42,0.55)] mb-6 relative overflow-hidden">
          <div className="pointer-events-none absolute -top-24 -right-24 h-56 w-56 rounded-full bg-white/10 blur-3xl" />
          <div className="pointer-events-none absolute -bottom-20 -left-16 h-48 w-48 rounded-full bg-white/5 blur-3xl" />
          <div className="relative flex items-start justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold tracking-tight text-white">Notification Preferences</h1>
              <p className="text-white/80 mt-1 text-sm">
                Choose which delivery channels reach you across every Orio360 notification.
              </p>
            </div>
            <div className="hidden sm:flex shrink-0 items-center gap-2 rounded-full bg-white/10 backdrop-blur-sm px-3 py-1.5 text-[11px] font-semibold tabular-nums">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 shadow-[0_0_8px_rgba(52,211,153,0.8)]" />
              Web Push · VAPID secured
            </div>
          </div>
        </div>

        {isLoading ? (
          <div className="space-y-4">
            <Skeleton variant="rect" height={260} className="rounded-2xl" />
          </div>
        ) : (
          <div className="space-y-6">
            <div className={cn(GLASS_FORM, 'p-5 sm:p-6 animate-slide-up')}>
              <div className="flex items-start justify-between mb-5">
                <div>
                  <h2 className="text-sm font-bold text-neutral-800 dark:text-neutral-100 uppercase tracking-wider">
                    Delivery Channels
                  </h2>
                  <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">
                    Master switches. Per-event overrides live in the matrix card below.
                  </p>
                </div>
              </div>

              <div className="space-y-3">
                {visibleChannels.map((channel) => (
                  <div key={channel.key} className="flex flex-col">
                    <div
                      className={cn(
                        'relative overflow-hidden rounded-xl bg-white/60 dark:bg-neutral-800/50 backdrop-blur-sm px-4 py-3 flex items-start gap-3',
                        "before:content-[''] before:absolute before:inset-y-0 before:left-0 before:w-1",
                        channelAccent(channel.tone),
                      )}
                    >
                      <div
                        className={cn(
                          'w-10 h-10 rounded-lg flex items-center justify-center shrink-0',
                          channelIconHalo(channel.tone),
                        )}
                      >
                        <svg
                          className="h-5 w-5"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth={2}
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          aria-hidden="true"
                        >
                          <path d={channel.iconPath} />
                        </svg>
                      </div>
                      <Toggle
                        checked={prefs[channel.key]}
                        onChange={(checked) => handleToggle(channel.key, checked)}
                        label={channel.label}
                        description={channel.description}
                        disabled={savingKey === channel.key}
                        className="w-full flex-row-reverse justify-between"
                      />
                    </div>

                    {/* G2.12 — push status row sits directly under the push toggle */}
                    {channel.key === 'push_enabled' && (
                      <div className="ml-[52px]">
                        <PushStatusRow
                          state={pushUiState}
                          errorDetail={pushError}
                          onRetry={() => handleToggle('push_enabled', true)}
                        />
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {!canUsePush && (
                <div className="mt-5 rounded-xl bg-indigo-50/60 dark:bg-indigo-900/20 backdrop-blur-sm px-4 py-3">
                  <p className="text-xs text-indigo-700 dark:text-indigo-200 leading-relaxed">
                    <span className="font-semibold">Browser push is unavailable for platform roles.</span>{' '}
                    Platform administrators receive notifications via the in-app bell and the
                    configured email channel — per-device Web Push is a tenant-scoped feature.
                  </p>
                </div>
              )}
            </div>

            {/* G2.10 — per-type channel matrix. Tenant-scoped + MANAGER+ only. */}
            {canSeeMatrix && <NotificationMatrixCard globalPrefs={prefs} />}

            <div className={cn(GLASS, 'p-4')}>
              <p className="text-xs text-neutral-500 dark:text-neutral-400 tabular-nums">
                Changes save automatically. Delivery depends on your subscription plan and the
                configured integrations (your verified email account, Twilio for SMS, WhatsApp Business API,
                VAPID Web Push).
              </p>
            </div>
          </div>
        )}
      </div>
    </PageContainer>
  );
}

export function NotificationPrefsPage() {
  return (
    <ErrorBoundary>
      <NotificationPrefsContent />
    </ErrorBoundary>
  );
}
